import { useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { ContentAccounts, WeekDays } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { pluralize } from 'utils'
import { capitalize } from 'lodash'
import { useCategoryCreditCosts } from 'hooks/useCategoryCreditCosts'
import { AccountsDropdown } from './AccountsDropdown'
import {
  ContentCategory,
  CONTENT_CATEGORY_CONFIG,
  DEFAULT_CONTENT_TYPE_COUNTS,
  getAvailableContentCategories,
} from 'utils/brandPlans/contentCategories'
import { CATEGORY_PROVIDERS, CategoryRowState, ContentTypeSettingsMap } from './Types'
import { CATEGORY_TO_ACCOUNTS_KEY, buildContentAccounts } from './contentTypeDefaultsUtils'
import Styles from './ContentTypeDefaults.module.scss'
import { CATEGORY_ICONS } from 'screens/VirtualMarketerOnboarding/V4ReviewTopics/V4ReviewTopics'
import { Heading } from 'blaze-ui/Heading/Heading'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import { DaysDropdown } from './DaysDropdown'
import { useIsAvatarVideoAvailable } from 'hooks/useIsAvatarVideoAvailable'

interface ContentTypeDefaultsProps {
  initialSettings?: ContentTypeSettingsMap | null
  initialContentAccounts?: ContentAccounts
  defaultWeekDays: WeekDays
  onConfirm: (settings: ContentTypeSettingsMap, contentAccounts?: ContentAccounts) => void
  onBack: (settings: ContentTypeSettingsMap, contentAccounts?: ContentAccounts) => void
  isStrictBrandControl?: boolean
}

export const ContentTypeDefaults = ({
  initialSettings,
  initialContentAccounts,
  defaultWeekDays,
  onConfirm,
  onBack,
  isStrictBrandControl = false,
}: ContentTypeDefaultsProps) => {
  const isAvatarVideoAvailable = useIsAvatarVideoAvailable()
  const categories = getAvailableContentCategories(isStrictBrandControl, isAvatarVideoAvailable)
  const categoryCosts = useCategoryCreditCosts()
  const { data: integrationsData } = useExternalIntegrations()
  const allIntegrations = useMemo(() => integrationsData?.external_integrations ?? [], [integrationsData])

  const [rowState, setRowState] = useState<Record<ContentCategory, CategoryRowState>>(
    () =>
      Object.fromEntries(
        categories.map((category) => {
          const accountsKey = CATEGORY_TO_ACCOUNTS_KEY[category]
          return [
            category,
            {
              count: initialSettings?.[category]?.count ?? DEFAULT_CONTENT_TYPE_COUNTS[category],
              weekDays: initialSettings?.[category]?.weekDays ?? defaultWeekDays,
              accountIds: initialContentAccounts?.[accountsKey] ?? [],
            },
          ]
        }),
      ) as Record<ContentCategory, CategoryRowState>,
  )

  // Once integrations load, default to all available accounts selected (only if not already set)
  const accountsInitializedRef = useRef(false)
  useEffect(() => {
    if (accountsInitializedRef.current || !integrationsData) return

    accountsInitializedRef.current = true
    setRowState(
      (prev) =>
        Object.fromEntries(
          categories.map((category) => {
            const accountsKey = CATEGORY_TO_ACCOUNTS_KEY[category]
            if (initialContentAccounts?.[accountsKey] != null) return [category, prev[category]]
            if (prev[category].accountIds.length > 0) return [category, prev[category]]
            const ids = allIntegrations
              .filter((i) => CATEGORY_PROVIDERS[category].includes(i.provider as ExternalIntegrationEnum))
              .map((i) => i.id)
            return [category, { ...prev[category], accountIds: ids }]
          }),
        ) as Record<ContentCategory, CategoryRowState>,
    )
  }, [integrationsData, categories, allIntegrations, initialContentAccounts])

  const handleCountChange = (category: ContentCategory, count: number) => {
    setRowState((prev) => ({
      ...prev,
      [category]: { ...prev[category], count: Math.max(0, count) },
    }))
  }

  const handleDaysChange = (category: ContentCategory, weekDays: WeekDays) => {
    setRowState((prev) => ({ ...prev, [category]: { ...prev[category], weekDays } }))
  }

  const handleAccountsChange = (category: ContentCategory, accountIds: number[]) => {
    setRowState((prev) => ({ ...prev, [category]: { ...prev[category], accountIds } }))
  }

  const totalPosts = categories.reduce((sum, cat) => sum + rowState[cat].count, 0)
  const totalCredits = categories.reduce((sum, cat) => sum + rowState[cat].count * categoryCosts[cat], 0)

  // Both footer buttons return to the review step, which displays these counts and whose
  // credit estimate is derived from them — so both must carry the current values back.
  // Discarding on Back silently created campaigns that differed from the quote (BLZ-16231).
  const currentSelection = (): [ContentTypeSettingsMap, ContentAccounts | undefined] => {
    const settingFor = (category: ContentCategory) => {
      const state = rowState[category]

      return {
        count: state?.count ?? 0,
        weekDays: state?.weekDays ?? defaultWeekDays,
      }
    }

    const settings: ContentTypeSettingsMap = {
      still_image_post: settingFor('still_image_post'),
      carousel_post: settingFor('carousel_post'),
      feed_video_post: settingFor('feed_video_post'),
      short_form_video: settingFor('short_form_video'),
      avatar_video: settingFor('avatar_video'),
      stories: settingFor('stories'),
      newsletter: settingFor('newsletter'),
      blog_post: settingFor('blog_post'),
    }

    // Until the integrations query resolves every row still holds its initial empty
    // accountIds, and committing those would clear the campaign's publishing accounts.
    // Returning undefined leaves whatever the caller already had in place; the counts
    // above are unaffected and still persist.
    if (!accountsInitializedRef.current) return [settings, undefined]

    const contentAccounts = buildContentAccounts(
      Object.fromEntries(categories.map((cat) => [cat, rowState[cat].count > 0 ? rowState[cat].accountIds : []])),
    )

    return [settings, contentAccounts as ContentAccounts]
  }

  const handleConfirm = () => onConfirm(...currentSelection())

  const handleBack = () => onBack(...currentSelection())

  return (
    <>
      <Modal.Content fillAvailableSpace>
        <div className={Styles.table}>
          <div className={Styles.tableHeader}>
            <Text variant="secondary" color="var(--dark-60)">
              Content type
            </Text>
            <Text variant="secondary" color="var(--dark-60)">
              Accounts
            </Text>
            <Text variant="secondary" color="var(--dark-60)">
              Posts per week
            </Text>
            <Text variant="secondary" color="var(--dark-60)">
              Post days
            </Text>
          </div>

          {categories.map((category) => {
            const config = CONTENT_CATEGORY_CONFIG[category]
            const iconConfig = CATEGORY_ICONS[category]
            const Icon = iconConfig.icon
            const state = rowState[category]

            return (
              <div key={category} className={Styles.tableRow}>
                <div className={Styles.categoryCell}>
                  <Icon size={24} color={iconConfig.color} />
                  <Heading level={4}>{capitalize(config.label.toLowerCase())}</Heading>
                </div>

                <div className={Styles.accountsCell}>
                  <AccountsDropdown
                    contentCategory={category}
                    selectedIds={state.accountIds}
                    onChange={(ids) => handleAccountsChange(category, ids)}
                    isDisabled={state.count < 1}
                  />
                </div>

                <div className={Styles.counterCell}>
                  <CountInput
                    className={Styles.countInput}
                    name={`${category}-count`}
                    value={state.count}
                    onChange={(count) => handleCountChange(category, count)}
                    min={0}
                    max={20}
                    size="sm"
                    buttonSize="xs"
                    buttonVariant="tertiary"
                  />
                </div>

                <div className={Styles.daysCell}>
                  <DaysDropdown
                    weekDays={state.weekDays}
                    onChange={(days) => handleDaysChange(category, days)}
                    isDisabled={state.count < 1}
                  />
                </div>
              </div>
            )
          })}

          <div className={Styles.totalRow}>
            <Heading level={4}>Total</Heading>
            <div className={Styles.totalPosts}>
              <Text>
                {totalPosts} {pluralize('post', totalPosts)}
              </Text>
              <div className={Styles.totalCredits}>
                <Credits size={22} color="var(--dark-40)" />
                <Text>
                  {totalCredits} {pluralize('credit', totalCredits)}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={handleBack}>
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="lg" onPress={handleConfirm}>
            Save
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
